/**
 * Faucet profile -> drawing primitives. PURE.
 *
 * `faucetPrimitives(profile)` flattens a `faucetSpecs` profile into a list of
 * four primitive kinds that BOTH surfaces know how to draw — the 3D renderer
 * as meshes (`faucetRenderers.js`), the elevation as SVG
 * (`schematics/FaucetElevation.jsx`). Because the list is built once, the two
 * cannot disagree about which parts exist or where they sit.
 *
 * Frame (see faucetSpecs.js): x across the width, u along the reach (+z),
 * v up from the box BOTTOM.
 *
 *   { kind: 'box',  name, x: [x0, x1], u: [u0, u1], v: [v0, v1] }
 *   { kind: 'cyl',  name, axis: 'v' | 'x' | 'u', r, at: { x, u, v },
 *                   span: [s0, s1], taper? }
 *       axis 'v': a vertical cylinder at (at.x, at.u) spanning v in `span`
 *       axis 'x': a cylinder across the width at (at.u, at.v) spanning x
 *       axis 'u': a cylinder along the reach at (at.x, at.v) spanning u
 *       `taper`: top radius / bottom radius (axis 'v' only)
 *   { kind: 'bar',  name, r, from: { x, u, v }, dir: { dx, du, dv }, len }
 *       a lever: round stock with a rounded tip
 *   { kind: 'tube', name, r, x, path, frontFromV? }
 *       a spout swept along a `faucetPath` centreline in the u-v plane;
 *       end-on it is drawn only above `frontFromV` (the body hides the rest)
 */

import { pathEnd } from './faucetPath';

const IN = 0.0254;

function plateParts(profile) {
  const { plate, mount } = profile;
  if (!plate) return [];
  if (mount === 'wall') {
    return [
      {
        kind: 'box',
        name: 'faucet-wall-plate',
        x: [-plate.w / 2, plate.w / 2],
        u: [plate.u, plate.u + plate.t],
        v: [plate.v - plate.h / 2, plate.v + plate.h / 2],
      },
    ];
  }
  return [
    {
      kind: 'box',
      name: 'faucet-deck-plate',
      x: [-plate.w / 2, plate.w / 2],
      u: [plate.u - plate.d / 2, plate.u + plate.d / 2],
      v: [0, plate.h],
    },
  ];
}

function bodyParts(profile) {
  const out = [];
  const { flange, body } = profile;
  if (flange) {
    out.push({
      kind: 'cyl',
      name: 'faucet-flange',
      axis: 'v',
      r: flange.r,
      at: { x: 0, u: flange.u },
      span: [0, flange.h],
    });
  }
  if (body) {
    out.push({
      kind: 'cyl',
      name: 'faucet-body',
      axis: 'v',
      r: body.r,
      at: { x: 0, u: body.u },
      span: [flange ? flange.h : 0, body.topV],
    });
  }
  return out;
}

function spoutParts(profile) {
  const { spout, sprayHead } = profile;
  // `frontFromV`: seen end-on the tube is hidden inside the body below its
  // top, so the front elevation draws the column from there up.
  const out = [
    {
      kind: 'tube',
      name: 'faucet-spout',
      r: spout.r,
      x: 0,
      path: spout.path,
      frontFromV: profile.body ? profile.body.topV : undefined,
    },
  ];
  const end = pathEnd(spout.path);
  if (sprayHead) {
    out.push({
      kind: 'cyl',
      name: 'faucet-spray-head',
      axis: 'v',
      r: sprayHead.r,
      at: { x: 0, u: sprayHead.u },
      span: [sprayHead.bottomV, sprayHead.bottomV + sprayHead.len],
      taper: 0.85,
    });
  } else {
    out.push({
      kind: 'cyl',
      name: 'faucet-aerator',
      axis: 'v',
      r: spout.r * 1.1,
      at: { x: 0, u: end[0] },
      span: [end[1], end[1] + 0.2 * IN],
    });
  }
  return out;
}

function leverParts(profile) {
  const { lever } = profile;
  if (!lever) return [];
  const { hub } = lever;
  const hubPart =
    lever.kind === 'side'
      ? {
          kind: 'cyl',
          name: 'faucet-lever-hub',
          axis: 'x',
          r: hub.r,
          at: { u: hub.u, v: hub.v },
          span: [hub.x - hub.len / 2, hub.x + hub.len / 2],
        }
      : {
          kind: 'cyl',
          name: 'faucet-lever-hub',
          axis: 'v',
          r: hub.r,
          at: { x: hub.x, u: hub.u },
          span: [hub.v - hub.len / 2, hub.v + hub.len / 2],
        };
  return [hubPart, barPart('faucet-lever', lever.lever)];
}

function barPart(name, lever) {
  return {
    kind: 'bar',
    name,
    r: lever.r,
    from: { x: lever.from.x, u: lever.from.u, v: lever.from.v },
    dir: { dx: lever.dx || 0, du: lever.du || 0, dv: lever.dv },
    len: lever.len,
  };
}

function handleParts(handle, index) {
  const name = `faucet-handle-${index === 0 ? 'left' : 'right'}`;
  const out = [];
  if (handle.kind === 'deck') {
    if (handle.flange) {
      out.push({
        kind: 'cyl',
        name: `${name}-flange`,
        axis: 'v',
        r: handle.flange.r,
        at: { x: handle.x, u: handle.u },
        span: [0, handle.flange.h],
      });
    }
    out.push({
      kind: 'cyl',
      name: `${name}-stem`,
      axis: 'v',
      r: handle.stem.r,
      at: { x: handle.x, u: handle.u },
      span: [0, handle.stem.h],
    });
  } else {
    out.push({
      kind: 'cyl',
      name: `${name}-stem`,
      axis: 'u',
      r: handle.stem.r,
      at: { x: handle.x, v: handle.v },
      span: [handle.stem.u, handle.stem.u + handle.stem.len],
    });
  }
  out.push(barPart(name, handle.lever));
  return out;
}

function handShowerParts(profile) {
  const hs = profile.handShower;
  if (!hs) return [];
  return [
    {
      kind: 'cyl',
      name: 'faucet-hand-shower',
      axis: 'v',
      r: hs.r,
      at: { x: hs.x, u: hs.u },
      span: [hs.topV - hs.len, hs.topV],
      taper: 0.7,
    },
    {
      kind: 'cyl',
      name: 'faucet-hand-shower-cradle',
      axis: 'x',
      r: hs.cradleR,
      at: { u: hs.u, v: hs.topV - hs.len * 0.3 },
      span: [0, hs.x],
    },
  ];
}

/** Every drawable part of a faucet profile. */
export function faucetPrimitives(profile) {
  return [
    ...plateParts(profile),
    ...bodyParts(profile),
    ...spoutParts(profile),
    ...leverParts(profile),
    ...profile.handles.flatMap(handleParts),
    ...handShowerParts(profile),
  ];
}

/** Far end of a bar primitive. */
export function barEnd(bar) {
  return {
    x: bar.from.x + bar.dir.dx * bar.len,
    u: bar.from.u + bar.dir.du * bar.len,
    v: bar.from.v + bar.dir.dv * bar.len,
  };
}
